import { eTlfItemType } from "functions/api/enums";
import { MosaicTocSectionClass } from "./MosaicTocSectionClass";
import type { IMosaicTocExpanded, ITocActionFromSignalR } from "./interface";

/**
 * Applies a SignalR ToC update to local state.
 *
 * @param state - The current expanded ToC state.
 * @param action - The action containing the SignalR payload.
 * @returns The updated ToC state after applying the remote changes.
 */
export const toc_fromSignalR = (
  state: IMosaicTocExpanded,
  action: ITocActionFromSignalR,
): IMosaicTocExpanded => {
  const newState: IMosaicTocExpanded = { ...state };

  // Delete item if found
  if (newState.tocItems.findIndex((item) => item.id === action.deletedItemId) > -1) {
    const index = newState.tocItems.findIndex((item) => item.id === action.deletedItemId);
    newState.removedItems.push(...newState.tocItems.splice(index, 1));
  }

  action.items.forEach((item) => {
    const index = newState.tocItems.findIndex((currentItem) => currentItem.id === item.id);
    const current = newState.tocItems[index];

    // Update item when found
    if (
      index > -1 &&
      item.type === eTlfItemType.section &&
      current instanceof MosaicTocSectionClass
    ) {
      current.update({
        ...item,
        saved: true,
      });
    } else if (
      index > -1 &&
      item.type !== eTlfItemType.section &&
      current.type !== eTlfItemType.section
    ) {
      newState.tocItems[index] = {
        ...current,
        ...item,
        saved: true,
      };
    }
    // Add new item
    else if (item.type === eTlfItemType.section) {
      const newSection = new MosaicTocSectionClass({
        id: item.id,
        sectionNumber:
          item.tocNumber ??
          // istanbul ignore next -- SignalR section updates should include the section number.
          "",
        sectionLabel:
          item.tocLabel ??
          // istanbul ignore next -- SignalR section updates should include the section label.
          "",
        order:
          item.tocOrder ??
          // istanbul ignore next -- SignalR section updates should include the section order.
          0,
        primaryHierarchyId: newState.hierarchyId,
        changed: false,
      });
      newState.tocItems.push(newSection);
    } else {
      newState.tocItems.push(item);
    }
  });

  newState.receivedMessages.push(action.receivedMessage);
  newState.tocItems.sort(
    (first, second) =>
      (first.tocOrder ??
        // istanbul ignore next -- all ToC items are expected to have a numeric order.
        0) -
      (second.tocOrder ??
        // istanbul ignore next -- all ToC items are expected to have a numeric order.
        0),
  );
  newState.saving = false;

  return newState;
};
